import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import Link from '@alltrails/denali/components/Link';
import Modal from '@alltrails/modal/components/Modal/Modal';
import ModalStep from '@alltrails/modal/components/Modal/ModalStep';
import MultiStepModal from '@alltrails/modal/components/Modal/MultiStepModal';
import useMultiStepModal from '@alltrails/modal/hooks/useMultiStepModal';
import DifficultyInfoModalContent from '../DifficultyInfoModalContent/DifficultyInfoModalContent';
import WheelchairInfoModalContent from '../WheelchairInfoModalContent/WheelchairInfoModalContent';
import styles from './AllFiltersModal.module.scss';
import AllFiltersModalV2 from './AllFiltersModalV2';

export { formatRatingValue } from './formatRatingValue';

type AllFiltersModalProps = Pick<ComponentProps<typeof Modal>, 'isOpen' | 'onCloseRequest'> & {
  map?: MapRef;
  onSeeTrailsClick?: () => void;
};

const AllFiltersModal = ({ isOpen, map, onCloseRequest, onSeeTrailsClick }: AllFiltersModalProps) => {
  const languageRegionCode = useLanguageRegionCode();

  const { activeModal, goBack, navigateTo } = useMultiStepModal<'initial' | 'difficulty' | 'wheelchair'>('initial');

  return (
    <MultiStepModal
      isOpen={isOpen}
      onBackRequest={goBack}
      onCloseComplete={() => {
        navigateTo('initial');
      }}
      onCloseRequest={onCloseRequest}
    >
      {activeModal === 'initial' && (
        <AllFiltersModalV2 map={map} navigateTo={navigateTo} onCloseRequest={onCloseRequest} onSeeTrailsClick={onSeeTrailsClick} />
      )}
      {activeModal === 'difficulty' && (
        <ModalStep
          onCloseRequest={onCloseRequest}
          title={<FormattedMessage defaultMessage="Difficulty levels" />}
          subtitle={
            <div className={styles.difficultySubtitle}>
              <Typography variant="text200" color="secondary" component="div">
                <FormattedMessage
                  defaultMessage="Our team considers <a>many factors</a>, like distance, elevation gain, and trailgoer feedback, to assign a difficulty level to each trail."
                  values={{
                    a: chunks => (
                      <Link href={getHelpCenterUrl(languageRegionCode, 16596491196436)} target="_blank" variant="secondary">
                        {chunks}
                      </Link>
                    )
                  }}
                />
              </Typography>
              <Typography variant="text200" color="secondary">
                <FormattedMessage defaultMessage="These levels are meant to apply to a wide audience — your experience may differ." />
              </Typography>
            </div>
          }
        >
          <DifficultyInfoModalContent />
        </ModalStep>
      )}
      {activeModal === 'wheelchair' && (
        <ModalStep onCloseRequest={onCloseRequest} title={<FormattedMessage defaultMessage="Wheelchair friendly" />}>
          <WheelchairInfoModalContent />
        </ModalStep>
      )}
    </MultiStepModal>
  );
};

export default AllFiltersModal;
